import { MutableRefObject, useEffect, useRef } from "react"

export type useElementObserverConfig = {
  options?: IntersectionObserverInit
}

const defaultOptions: IntersectionObserverInit = {
  threshold: 0.1,
  rootMargin: "-20px 0px -20px 0px"
}

const useElementObserver = <T extends HTMLElement = HTMLElement>(
  elementToObserve: MutableRefObject<T | null>,
  callback: () => boolean | undefined,
  config: useElementObserverConfig = {}
) => {
  const { options } = config
  const elementIsSeen = useRef<boolean>(false)
  const intersectionObserver = useRef<IntersectionObserver | null>(null)

  useEffect(() => {
    if (!intersectionObserver.current) {
      intersectionObserver.current = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting && !elementIsSeen.current) {
              const observeOnce = callback?.()
              if (observeOnce) elementIsSeen.current = true
            }
          })
        },
        { ...defaultOptions, ...options }
      )
    }

    if (!elementToObserve.current) return
    intersectionObserver.current.observe(elementToObserve.current)

    return () => {
      if (!intersectionObserver.current || !elementToObserve.current) return
      intersectionObserver.current.unobserve(elementToObserve.current)
      intersectionObserver.current = null
    }
  }, [])
}

export default useElementObserver
